/**
 * Validation editor for a dynamic attribute.
 *
 * Exported so the Tier 1 settings pages (product templates today, host
 * templates / transactions / customers next) compose one implementation rather
 * than each growing their own.
 *
 * Every kind here maps onto a rule the backend already enforces at each EAV
 * write. Regex is deliberately absent: a tenant admin cannot review one, and an
 * unreviewable pattern is exactly how a stale format went unnoticed until
 * customers could not register.
 */

import { Check, Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { matchesSerialRanges } from "@/shared/lib/forms/validation-rules";
import {
  DRAFT_KIND_LABELS,
  boundTo,
  rangeError,
  draftKindsFor,
  emptyDraft,
  stripWildcards,
  type ValidationDraft,
  type ValidationDraftKind,
} from "./attribute-validation-rules";

/** Example text, so the rule needs no explanatory line under it. */
const KIND_PLACEHOLDERS: Record<
  ValidationDraftKind,
  { key: string; text: string }
> = {
  serial_range: { key: "", text: "" },
  starts_with: {
    key: "templates.validation.placeholder.startsWith",
    text: "e.g. ABC",
  },
  ends_with: {
    key: "templates.validation.placeholder.endsWith",
    text: "e.g. -XY",
  },
  min_length: {
    key: "templates.validation.placeholder.length",
    text: "Characters",
  },
  max_length: {
    key: "templates.validation.placeholder.length",
    text: "Characters",
  },
};

export function AttributeValidationEditor({
  fieldType,
  drafts,
  onChange,
}: {
  fieldType: string;
  drafts: ValidationDraft[];
  onChange: (drafts: ValidationDraft[]) => void;
}) {
  const { t } = useTranslation();
  // Per-rule scratch input: lets an admin check a real code against the rows
  // as typed, before saving. Never persisted.
  const [probes, setProbes] = useState<Record<number, string>>({});
  const kindLabel = (kind: ValidationDraftKind) =>
    t(DRAFT_KIND_LABELS[kind].key, DRAFT_KIND_LABELS[kind].label);

  const kinds = draftKindsFor(fieldType);
  if (kinds.length === 0) return null;

  function update(index: number, next: ValidationDraft) {
    onChange(drafts.map((draft, i) => (i === index ? next : draft)));
  }

  function remove(index: number) {
    onChange(drafts.filter((_, i) => i !== index));
  }

  return (
    <div data-testid="attribute-validation-editor">
      <div className="mb-1.5 flex items-center justify-between">
        <Label className="clara-section-label block">
          {t("templates.validation.title", "Validation")}
        </Label>
        <Select
          value=""
          onValueChange={(kind: string) =>
            onChange([...drafts, emptyDraft(kind as ValidationDraftKind)])
          }
        >
          <SelectTrigger
            className="h-7 w-[150px] text-[12px]"
            data-testid="attribute-validation-add"
          >
            <SelectValue
              placeholder={t("templates.validation.addRule", "Add rule")}
            />
          </SelectTrigger>
          <SelectContent>
            {kinds.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {kindLabel(kind)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {drafts.length === 0 ? (
        <p className="rounded-[4px] border-[0.5px] border-dashed border-[var(--rule-strong)] px-3 py-2.5 text-[11px] text-[var(--ink-3)]">
          {t("templates.validation.empty", "No rules. Any value is accepted.")}
        </p>
      ) : (
        // Capped so the dialog stops growing once a few rules are added; the
        // list scrolls instead of pushing Save down the page.
        <div className="clara-scroll flex max-h-[38vh] flex-col gap-1.5 overflow-y-auto">
          {drafts.map((draft, index) => (
            <div
              key={index}
              className="flex flex-col gap-1.5 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-2.5 py-2"
              data-testid={`attribute-validation-rule-${draft.kind}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[var(--ink)]">
                  {kindLabel(draft.kind)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${kindLabel(draft.kind)}`}
                  onClick={() => remove(index)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>

              {draft.kind === "serial_range" && (
                <div className="flex flex-col gap-1">
                  {draft.ranges.map((bound, boundIndex) => (
                    <div key={boundIndex} className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={bound.from}
                          placeholder="From"
                          aria-label={`Range ${boundIndex + 1} from`}
                          className="h-8 text-[12px]"
                          onChange={(e) =>
                            update(index, {
                              ...draft,
                              ranges: draft.ranges.map((r, i) =>
                                i === boundIndex
                                  ? { ...r, from: e.target.value }
                                  : r,
                              ),
                            })
                          }
                        />
                        <Input
                          value={bound.to}
                          placeholder={t(
                            "templates.validation.toPlaceholder",
                            "To (blank = one code)",
                          )}
                          aria-label={`Range ${boundIndex + 1} to`}
                          className="h-8 text-[12px]"
                          onChange={(e) =>
                            update(index, {
                              ...draft,
                              ranges: draft.ranges.map((r, i) =>
                                i === boundIndex
                                  ? { ...r, to: e.target.value }
                                  : r,
                              ),
                            })
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`Remove range ${boundIndex + 1}`}
                          disabled={draft.ranges.length === 1}
                          onClick={() =>
                            update(index, {
                              ...draft,
                              ranges: draft.ranges.filter(
                                (_, i) => i !== boundIndex,
                              ),
                            })
                          }
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      {rangeError(bound, t) ? (
                        <p
                          className="text-[10px] text-[var(--status-error)]"
                          data-testid={`attribute-validation-range-error-${boundIndex}`}
                        >
                          {rangeError(bound, t)}
                        </p>
                      ) : null}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    className="self-start"
                    data-testid="attribute-validation-add-range"
                    onClick={() =>
                      update(index, {
                        ...draft,
                        ranges: [...draft.ranges, { from: "", to: "" }],
                      })
                    }
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    {t("templates.validation.addRange", "Add range")}
                  </Button>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={probes[index] ?? ""}
                      placeholder={t(
                        "templates.validation.tryCode",
                        "Try a code",
                      )}
                      aria-label="Try a code"
                      className="h-8 text-[12px]"
                      data-testid="attribute-validation-probe"
                      onChange={(e) =>
                        setProbes((prev) => ({
                          ...prev,
                          [index]: e.target.value,
                        }))
                      }
                    />
                    {(probes[index] ?? "").trim() ===
                    "" ? null : matchesSerialRanges(
                        probes[index],
                        draft.ranges
                          .filter(
                            (r) =>
                              r.from.trim() !== "" && rangeError(r) === null,
                          )
                          .map((r) => ({
                            from: r.from.trim(),
                            to: boundTo(r),
                          })),
                      ) ? (
                      <span
                        className="flex shrink-0 items-center gap-1 text-[11px] text-[var(--status-success)]"
                        data-testid="attribute-validation-probe-result"
                      >
                        <Check className="h-3 w-3" />
                        {t("templates.validation.probeAccepted", "Accepted")}
                      </span>
                    ) : (
                      <span
                        className="flex shrink-0 items-center gap-1 text-[11px] text-[var(--status-error)]"
                        data-testid="attribute-validation-probe-result"
                      >
                        <X className="h-3 w-3" />
                        {t(
                          "templates.validation.probeRejected",
                          "Not accepted",
                        )}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[var(--ink-3)]">
                    {t(
                      "templates.validation.rangesHint",
                      "A code is accepted if it matches any row. Leave To blank to allow a single code.",
                    )}
                  </p>
                </div>
              )}

              {(draft.kind === "starts_with" || draft.kind === "ends_with") && (
                <Input
                  value={draft.value}
                  placeholder={t(
                    KIND_PLACEHOLDERS[draft.kind].key,
                    KIND_PLACEHOLDERS[draft.kind].text,
                  )}
                  aria-label={kindLabel(draft.kind)}
                  className="h-8 text-[12px]"
                  onChange={(e) =>
                    update(index, {
                      ...draft,
                      value: stripWildcards(e.target.value),
                    })
                  }
                />
              )}

              {(draft.kind === "min_length" || draft.kind === "max_length") && (
                <Input
                  value={draft.length}
                  type="number"
                  min={0}
                  placeholder={t(
                    KIND_PLACEHOLDERS[draft.kind].key,
                    KIND_PLACEHOLDERS[draft.kind].text,
                  )}
                  aria-label={kindLabel(draft.kind)}
                  className="h-8 text-[12px]"
                  onChange={(e) =>
                    update(index, { ...draft, length: e.target.value })
                  }
                />
              )}

              <Input
                value={draft.message}
                placeholder={t(
                  "templates.validation.message",
                  "Message shown if invalid",
                )}
                aria-label={`${kindLabel(draft.kind)} message`}
                className="h-8 text-[12px]"
                onChange={(e) =>
                  update(index, { ...draft, message: e.target.value })
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
